import type {
  IntegrationOption,
  NotificationSetting,
  SelectOption,
} from './types';

export const supportCategories = [
  'General Inquiry',
  'Concierge Related Issue',
  'Student Related Issue',
  'Technical Issue',
  'Feedback or Complaint',
];

export const supportFaqs = [
  {
    question: 'How do I schedule a session with a student?',
    answer:
      'Sessions are scheduled by students based on the availability you set. Once a student books a slot, the session appears under Sessions > Upcoming.',
  },
  {
    question: 'Can I cancel or reschedule a session?',
    answer:
      "Yes, you can cancel or reschedule sessions before the session starts. Go to the Sessions tab, select the session, and choose 'Reschedule' or 'Cancel'. Students will be notified automatically.",
  },
  {
    question: 'How do I view student feedback?',
    answer:
      "Student reviews are private and visible only to the concierge team. This is intentional and part of Onefinet's privacy-first approach.",
  },
  {
    question: "What happens if a student doesn't attend a session?",
    answer:
      'If a student does not join the session, it is marked complete and deducted from your session pool. You are not penalised.',
  },
];

export const supportEmail = 'Coaching@example.com';

export const supportCategoryOptions: SelectOption[] = supportCategories.map(
  (item) => ({
    value: item,
    label: item,
  })
);

export const emailNotificationOptions: NotificationSetting[] = [
  {
    key: 'emailMatchAlert',
    label: 'New Match Alert',
    description: "Get notified when a student's profile is matched",
  },
  {
    key: 'emailUpcomingSessionReminder',
    label: 'Upcoming Session Reminders',
    description: 'Receive reminders 24 hours before scheduled sessions',
  },
  {
    key: 'emailSessionCancellation',
    label: 'Session Cancellation Alerts',
    description: 'Get notified when a student cancels a session',
  },
  {
    key: 'emailReschedulingRequest',
    label: 'Rescheduling Requests',
    description: 'Get notified when students request to reschedule',
  },
  {
    key: 'emailPlatformAnnouncement',
    label: 'Important platform announcements',
    description: 'Product updates or policy changes from Onefinnet.',
  },
  {
    key: 'emailNewMessage',
    label: 'New messages',
    description: 'Real-time notifications for incoming student messages.',
  },
];

export const appNotificationOptions: NotificationSetting[] = [
  {
    key: 'inAppUpcomingSessionReminder',
    label: 'Upcoming session alerts',
    description: 'Get notified before upcoming sessions.',
  },
  {
    key: 'inAppSessionChanges',
    label: 'Session changes',
    description: 'Instant alerts for bookings, reschedules, or cancellations.',
  },
  {
    key: 'inAppNewStudentAssigned',
    label: 'New student assigned',
    description: 'In-app alert when a new student is added to your account.',
  },
  {
    key: 'inAppNewMessage',
    label: 'New messages',
    description: 'Real-time notifications for incoming student messages.',
  },
  {
    key: 'inAppActionRequired',
    label: 'Action required',
    description: 'Alerts when you need to complete a task (e.g., mark session complete).',
  },
  {
    key: 'inAppAttendanceUpdate',
    label: 'Warnings or attendance updates',
    description: 'Notifications related to session attendance or recorded warnings.',
  },
  {
    key: 'inAppPlatformUpdate',
    label: 'Platform updates',
    description: 'Important service or feature updates.',
  },
];

export const integrationOptions: IntegrationOption[] = [
  {
    key: 'googleCalendar',
    label: 'Google Calendar',
    description:
      'Integrate Google Calendar for instant updates to your coaching team.',
    icon: 'G',
    iconBg: 'bg-amber-50',
    iconText: 'text-amber-600',
  },
  {
    key: 'outlookCalendar',
    label: 'Outlook Calendar',
    description:
      'Integrate Outlook Calendar for notifications, updates and scheduling of sessions with trainees.',
    icon: 'O',
    iconBg: 'bg-blue-50',
    iconText: 'text-blue-600',
  },
  {
    key: 'zoomCalendar',
    label: 'Zoom Calendar',
    description:
      'Integrate Zoom Calendar for notifications, updates and scheduling of sessions with trainees.',
    icon: 'Z',
    iconBg: 'bg-sky-50',
    iconText: 'text-sky-600',
  },
  {
    key: 'appleCalendar',
    label: 'Apple Calendar',
    description:
      'Integrate Apple Calendar for notifications, updates and scheduling of sessions with trainees.',
    icon: 'A',
    iconBg: 'bg-slate-100',
    iconText: 'text-slate-600',
  },
];
